/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */

import {GenericObject} from '@ion/core/src/types/entities';
import * as logger from './SocketLogger';
import {
  EntitySubscription,
  ObservableEntityId,
  ObserverMap,
  OnDataEvent
} from './types';

/**
 * Defines the initial (empty) state of the observer mappings for each of the
 * observable entity types.
 *
 * Note, the declaration order carries no meaning; every consumer resolves an
 * entity type explicitly rather than by the position of its key.
 */
const initialObservers = (): ObserverMap => ({
  [EntitySubscription.asset]       : {},
  [EntitySubscription.task]        : {},
  [EntitySubscription.jobProgress] : {},
  [EntitySubscription.jobStatus]   : {}
});

/**
 * Declares the precedence of each observable entity type within the identifier
 * only lookup, whereby the lowest precedence is consulted first, and `null`
 * denotes a type which is ineligible for that lookup entirely.
 *
 * Declared as a Record over every EntitySubscription so that a newly added
 * entity type fails to compile until its eligibility has been stated; the
 * declaration is thus the enforcement, rather than a convention which a later
 * addition can silently omit.
 *
 * Every type is currently eligible, and the precedence of each preserves the
 * resolution order the lookup has always applied. Note, an unmapped change can
 * only be resolved by the id alone, and is thus delivered to whichever eligible
 * type happens to hold that id first; declaring a type ineligible (`null`) is
 * therefore the means of excluding one whose observers must never receive a
 * change of an unrecognized shape.
 *
 * @see LEGACY_ENTITIES
 * @see DISCRIMINATOR_ENTITIES
 */
const LEGACY_PRECEDENCE: Readonly<Record<EntitySubscription, number | null>> = {
  [EntitySubscription.asset]       : 0,
  [EntitySubscription.task]        : 1,
  [EntitySubscription.jobProgress] : 2,
  [EntitySubscription.jobStatus]   : 3
};

/**
 * Defines the entity types eligible for the identifier only lookup, ordered by
 * their declared precedence.
 *
 * Note, the order is derived from the precedence declared against each type
 * rather than from the position of its key, so that neither this collection
 * nor the observer mappings carry meaning in their declaration order.
 *
 * @see LEGACY_PRECEDENCE
 */
const LEGACY_ENTITIES: readonly EntitySubscription[] =
  (Object.keys(LEGACY_PRECEDENCE) as EntitySubscription[])
    .filter(entity => LEGACY_PRECEDENCE[entity] !== null)
    .sort((first, second) => (
      (LEGACY_PRECEDENCE[first] ?? 0) - (LEGACY_PRECEDENCE[second] ?? 0)
    ));

/**
 * The Observers API manages the state of all WebSocket subscriptions based on the
 * id of the entity being observed. Note, this API is primarily only intended to
 * be used internally by the lower-level EntityObserver API.
 */
export default class Observers {
  /**
   * Defines the mapping of observed ids to collections of OnDataEvent function
   * associations which will be invoked upon receiving a message for the given
   * id.
   */
  private observers: ObserverMap = initialObservers();

  /**
   * Adds an observer for the given id (or collection of ids) for the provided
   * observer function. When a message is received which matches the specified
   * id, the observer will be invoked with the change.
   *
   * @param entity The type of entity for which the observer is to be added.
   * @param ids Either a single id, or array of ids to be observed for changes.
   * @param onData Callback function which will be invoked upon receiving changes
   *        for the given ids.
   */
  addObserver = (entity: EntitySubscription, ids: ObservableEntityId[], onData: OnDataEvent) => {
    const entities = this.observers[entity];
    ids?.forEach((id: ObservableEntityId) => {
      const observers = entities[id];
      if (!observers?.length) {
        entities[id] = [onData];
      } else {
        observers.push(onData);
      }
    });
  };

  /**
   * Removes a previously added observer for the given id (or collection of ids).
   * If the provided id(s) no longer have any observers associated with them, an
   * object containing the obsolete ids will be returned.
   *
   * @param   entity The type of entity for which the observer is to be removed.
   * @param   ids Either a single id, or array of ids which are no longer to be
   *          observed for changes.
   * @param   onData The callback function previously passed to `addObserver` for
   *          the given id(s).
   * @returns An object containing the `ids` (as keys) for all entities which are
   *          no longer being observed and are to be removed (if any), otherwise,
   *          undefined.
   */
  removeObserver = (entity: EntitySubscription, ids: ObservableEntityId[], onData?: OnDataEvent): GenericObject => {
    const entities = this.observers[entity];
    const obsolete: GenericObject = {};
    ids?.forEach((id: ObservableEntityId) => {
      const observers = entities[id];
      if (observers?.length) {
        entities[id] = observers.filter(registered => registered !== onData);
        if (!entities[id].length || !onData) {
          obsolete[id] = true;
          delete entities[id];
        }
      }
    });
    return obsolete;
  };

  /**
   * Invokes all currently registered observers for the given entity type and
   * id. Routing by both the entity type and the id ensures that multiple entity
   * types observing the same id each only receive their own changes.
   *
   * When the entity type could not be resolved for the received message, the
   * observers are resolved via the legacy identifier only lookup instead. Both
   * paths resolve a collection of observers and then notify it, so that the
   * routing of a change remains separate from the resolution of its observers.
   *
   * @param entity The type of entity for which the observers are to be invoked,
   *               or `undefined` when the entity type could not be resolved.
   * @param id     The normalized (stringified) id of the entity which changed.
   * @param change The consolidated changes received for the given entity id.
   */
  invokeObservers = (
    entity: EntitySubscription | undefined, id: string, change: GenericObject): void => {
    const observers = entity
      ? this.observers[entity]?.[id]
      : this.resolveLegacyObservers(id);
    this.notifyObservers(observers, id, change);
  };

  /**
   * @private
   *
   * Resolves the observers of a change whose entity type could not be resolved,
   * by locating the id under each eligible entity type in precedence order.
   *
   * @returns The observers of the first eligible entity type which holds the
   *          given id, otherwise, `undefined`; a change whose id is held by no
   *          eligible type is thereby dropped, as the type to which it belongs
   *          cannot be inferred from the id alone.
   *
   * @see LEGACY_ENTITIES
   */
  private resolveLegacyObservers = (id: string): OnDataEvent[] | undefined => {
    logger.unmappedEntity(id);
    return LEGACY_ENTITIES
      .map(entity => this.observers[entity]?.[id])
      .find(observers => observers?.length);
  };

  /**
   * @private
   *
   * Invokes each of the provided observers with the received change and the id
   * of the entity to which the change applies.
   */
  private notifyObservers = (
    observers: OnDataEvent[] | undefined, id: string, change: GenericObject): void => {
    observers?.forEach(observer => {
      observer(change, id);
    });
  };

  /**
   * Removes all observers registered for the given entity type, leaving the
   * observers of all other entity types intact.
   *
   * @param   entity The type of entity for which all observers are to be
   *          removed.
   * @returns An array of the entity ids which were being observed, and are thus
   *          to be unsubscribed.
   */
  removeAllObservers = (entity: EntitySubscription): ObservableEntityId[] => {
    const identifiers = this.getObservedIds(entity);
    this.observers[entity] = {};
    return identifiers;
  };

  /**
   * Removes all observers for every entity type, effectively restoring the
   * instance to its initial state. Generally, this is only to be invoked upon
   * signout.
   */
  reset = (): void => {
    this.observers = initialObservers();
  };

  /**
   * Determines if any observers are currently registered for the entity of the
   * given id.
   */
  hasObserver(entity: EntitySubscription, id: ObservableEntityId, observer: OnDataEvent): boolean {
    return !!(this.observers[entity]?.[id]?.find(item => item === observer));
  };

  /**
   * Returns the number of current observers for the entity of the given id.
   */
  getObserverCount(entity: EntitySubscription, id: ObservableEntityId): number {
    return this.observers[entity]?.[id]?.length || 0;
  };

  /**
   * Provides a utility which returns all currently observed entity ids.
   *
   * @returns An array of entity ids currently being observed.
   */
  getObservedIds(entity: EntitySubscription): ObservableEntityId[] {
    return Object.keys(this.observers[entity]);
  }

  /**
   * Provides a utility which returns a collection of obsolete entity ids; i.e.
   * ids which are no longer being observed.
   *
   * @param   obsolete An object containing the ids of entities which are no
   *          longer being observed.
   * @returns An array of obsolete entity ids (if any), otherwise, an empty
   *          array is returned.
   */
  static getObsoleteIds(obsolete: GenericObject): ObservableEntityId[] {
    return Object.keys(obsolete);
  }
}
